import { useEffect, useMemo, useRef, useState } from "react";
import { Container, MainContent } from "./EditorStyles";
import { ReactComponent as PanoramaIcon } from "./../../images/editor-icons/camera.svg";
import { ReactComponent as MapIcon } from "./../../images/editor-icons/map.svg";
import { ReactComponent as PoiIcon } from "./../../images/editor-icons/map-pin.svg";
import { ReactComponent as ChangesIcon } from "./../../images/editor-icons/history.svg";
import { Redirect, Switch, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Route } from "react-router-dom";
import classNames from "classnames";
import Header from "../../shared/components/Header";
import { MemoizedNavigation } from "../Navigation/Navigation";
import { StyledLayoutSidebar, StyledSidebar } from "../Sidebar/SidebarStyles";
import { NavigationItemIcon, NavigationItemText, StyledLink } from "../Navigation/NavigationStyles";
import SinglePanoPage from "./Pano/SinglePanoPage";
import MainContentPlaceholder from "./BaseComponents/MainContentPlaceholder";
import SingleMapPage from "./Map/SingleMapPage";
import PanoPage from "./Pano/PanoOverview";
import MapPage from "./Map/MapOverview";
import Changes from "./Changes/ChangesOverview";
import MapEditor from "./Map/MapEditor";
import CloseEditorButton from "./BaseComponents/CloseEditorButton";
import { useAppState } from "../../AppContext";
import { useInterfaceState } from "../../InterfaceContext";
import { MapDataProvider } from "./Map/MapDataContext";
import PoiOverview from "./Poi/PoiOverview";
import SinglePoiPage from "./Poi/SinglePoiPage";

const Editor = () => {
  const { state } = useAppState();
  const { interfaceState } = useInterfaceState();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  const homeLink = useMemo(() => {
    if (state.landingPage) {
      return "/@";
    }
    if (interfaceState.currentEntrance) {
      return interfaceState.currentEntrance.type !== "map"
        ? "/pano/" + interfaceState.currentEntrance.pano.slug
        : "/map";
    }
    return "/";
  }, [interfaceState.currentEntrance, state.landingPage]);
  
  return (
    <Container>
      <StyledLayoutSidebar className="is-open">
        <StyledSidebar>
          <Header
            logoPath={state.logo}
            alt={state.name}
            description={state.description}
            homeUrlPath={homeLink}
          >
            {state.name}
          </Header>
          <MemoizedNavigation>
            <StyledLink
              to="/editor/pano"
              className={classNames({
                active: location.pathname.startsWith("/editor/pano"),
              })}
            >
              <NavigationItemIcon>
                <PanoramaIcon />
              </NavigationItemIcon>
              <NavigationItemText>
                <FormattedMessage
                  id="editor.nav.panos"
                  defaultMessage="Panoramas"
                  description="Nav bar text for panoramas overview"
                />
              </NavigationItemText>
            </StyledLink>
            <StyledLink
              to="/editor/map"
              className={classNames({
                active: location.pathname.startsWith("/editor/map"),
              })}
            >
              <NavigationItemIcon>
                <MapIcon />
              </NavigationItemIcon>
              <NavigationItemText>
                <FormattedMessage
                  id="editor.nav.maps"
                  defaultMessage="Map"
                  description="Nav bar text for map overview"
                />
              </NavigationItemText>
            </StyledLink>
            <StyledLink
              to="/editor/poi"
              className={classNames({
                active: location.pathname.startsWith("/editor/poi"),
              })}
            >
              <NavigationItemIcon>
                <PoiIcon />
              </NavigationItemIcon>
              <NavigationItemText>
                <FormattedMessage
                  id="editor.nav.pois"
                  defaultMessage="Pois"
                  description="Nav bar text for poi overview"
                />
              </NavigationItemText>
            </StyledLink>
            <StyledLink
              to="/editor/changes"
              className={classNames({
                active: location.pathname.startsWith("/editor/changes"),
              })}
            >
              <NavigationItemIcon>
                <ChangesIcon />
              </NavigationItemIcon>
              <NavigationItemText>
                <FormattedMessage
                  id="editor.nav.changes"
                  defaultMessage="Changes"
                  description="Nav bar text for changes overview"
                />
              </NavigationItemText>
            </StyledLink>
          </MemoizedNavigation>
          {location.pathname.startsWith("/editor/pano") ? (
              <PanoPage />
            ) : location.pathname.startsWith("/editor/map") ? (
              <MapPage />
            ) : location.pathname.startsWith("/editor/poi") ? (
              <PoiOverview />
            ) : (
              <Changes />
            )}
        </StyledSidebar>
      </StyledLayoutSidebar>
      <div style={{ position: "fixed", top: "0", right: "32px", zIndex: "1000" }}>
        <CloseEditorButton/>
      </div>
      <MainContent style={{backgroundColor: "var(--color-primary-20)"}}>
        <Switch>
          <Route exact path="/editor">
            <Redirect to="/editor/pano" />
          </Route>
          <Route path="/editor/pano/:id/:scenarioSlug" component={SinglePanoPage} />
          <Route path="/editor/pano" component={MainContentPlaceholder} />
          <Route path="/editor/poi/:id" component={SinglePoiPage} />
          <Route path="/editor/poi" component={MainContentPlaceholder} />
          <Route path="/editor/changes" component={MainContentPlaceholder} />
          <MapDataProvider>
            <Route path="/editor/map/:id/:scenarioSlug" component={SingleMapPage} />
            <Route path="/editor/map" component={MapEditor} />
          </MapDataProvider>
        </Switch>
      </MainContent>
    </Container>
  );
};

export default Editor;
